import React from 'react';
import { buildRadarAxes } from '../lib/dailyPracticeReport';
import { useI18n } from '../i18n/I18nProvider';

const WIDTH = 800;
const HEIGHT = 640;
const CENTER_X = 400;
const CENTER_Y = 300;
const RADIUS = 190;

const pointAt = (index, count, radius) => {
  const angle = (-Math.PI / 2) + (index * Math.PI * 2 / count);
  return {
    x: CENTER_X + Math.cos(angle) * radius,
    y: CENTER_Y + Math.sin(angle) * radius,
  };
};

const polygonPoints = (axes, field, scale = 1) => axes.map((axis, index) => {
  const point = pointAt(index, axes.length, RADIUS * scale * Number(axis[field] || 0));
  return `${point.x},${point.y}`;
}).join(' ');

const DailyPracticeRadar = ({ report, studentName }) => {
  const { t } = useI18n();
  const axes = buildRadarAxes(report, t);
  return (
    <div className="min-w-0">
      <div className="mb-2 flex flex-wrap items-center justify-center gap-5 text-xs font-medium">
        <span className="inline-flex items-center gap-2 text-blue-800"><span className="h-2.5 w-5 bg-blue-600" />{studentName || t('Student')}</span>
        <span className="inline-flex items-center gap-2 text-emerald-800"><span className="h-2.5 w-5 bg-emerald-500" />{t('Class average')}</span>
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={t('Daily practice radar comparing {student} with the class average', { student: studentName || t('the student') })}
        className="h-auto w-full"
      >
        {[0.2, 0.4, 0.6, 0.8, 1].map((scale) => (
          <polygon
            key={scale}
            points={axes.map((_, index) => {
              const point = pointAt(index, axes.length, RADIUS * scale);
              return `${point.x},${point.y}`;
            }).join(' ')}
            fill={scale % 0.4 === 0 ? '#f8fafc' : 'none'}
            stroke="#cbd5e1"
            strokeWidth="1"
          />
        ))}
        {axes.map((axis, index) => {
          const edge = pointAt(index, axes.length, RADIUS);
          const label = pointAt(index, axes.length, RADIUS + 70);
          const anchor = label.x < CENTER_X - 20 ? 'end' : label.x > CENTER_X + 20 ? 'start' : 'middle';
          return (
            <g key={axis.key}>
              <line x1={CENTER_X} y1={CENTER_Y} x2={edge.x} y2={edge.y} stroke="#dbe2ea" />
              <text x={label.x} y={label.y - 7} textAnchor={anchor} fill="#334155" fontSize="13" fontWeight="600">
                <tspan x={label.x}>{axis.label}</tspan>
                <tspan x={label.x} dy="17" fill="#2563eb" fontSize="11" fontWeight="500">
                  {`${t('Student')} ${axis.format(axis.studentRaw)}`}
                </tspan>
                <tspan x={label.x} dy="15" fill="#059669" fontSize="11" fontWeight="500">
                  {`${t('Class')} ${axis.format(axis.classRaw)}`}
                </tspan>
              </text>
            </g>
          );
        })}
        <polygon points={polygonPoints(axes, 'classRatio')} fill="#10b981" fillOpacity="0.13" stroke="#059669" strokeWidth="2" />
        <polygon points={polygonPoints(axes, 'studentRatio')} fill="#2563eb" fillOpacity="0.18" stroke="#2563eb" strokeWidth="2.5" />
        {axes.map((axis, index) => {
          const studentPoint = pointAt(index, axes.length, RADIUS * axis.studentRatio);
          const classPoint = pointAt(index, axes.length, RADIUS * axis.classRatio);
          return (
            <g key={`${axis.key}-points`}>
              <circle cx={classPoint.x} cy={classPoint.y} r="3.5" fill="#059669"><title>{`${t('Class')}: ${axis.format(axis.classRaw)}`}</title></circle>
              <circle cx={studentPoint.x} cy={studentPoint.y} r="4" fill="#2563eb"><title>{`${studentName || t('Student')}: ${axis.format(axis.studentRaw)}`}</title></circle>
            </g>
          );
        })}
      </svg>
      <p className="text-center text-xs leading-relaxed text-gray-500">
        {t('Counts are scaled per axis; accuracy uses 0-100%. Harder tiers extend farther outward.')}
      </p>
      <div className="mt-4 overflow-hidden border-y border-gray-200 text-xs">
        <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3 bg-gray-50 px-2 py-2 font-semibold text-gray-600">
          <span>{t('Measure')}</span><span className="text-blue-700">{t('Student')}</span><span className="text-emerald-700">{t('Class')}</span>
        </div>
        {axes.map((axis) => (
          <div key={`${axis.key}-values`} className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 border-t border-gray-100 px-2 py-2">
            <span className="min-w-0 text-gray-700">{axis.label}</span>
            <span className="whitespace-nowrap font-medium tabular-nums text-blue-700">{axis.format(axis.studentRaw)}</span>
            <span className="whitespace-nowrap font-medium tabular-nums text-emerald-700">{axis.format(axis.classRaw)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DailyPracticeRadar;
